import { useEffect, useMemo, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { NAV, PATRIKA, PANCHANGA, PANCHANGA_PREMIUM, ABOUT_LINKS } from "@/lib/nav";
import RichTextEditor from "@/components/RichTextEditor";
import { LayoutList, Save, Loader2, Eye, EyeOff, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

// Festival landing pages — reachable via homepage promos, not the main NAV,
// so they must be registered here explicitly to appear in the Pages Manager.
const FESTIVAL_GUIDES = [
  { to: "/ganesh-chaturthi", en: "Ganesh Chaturthi" },
  { to: "/navratri", en: "Navratri" },
  { to: "/pitru-paksha", en: "Pitru Paksha" },
];

// Standalone / utility pages reachable outside the main NAV groups — registered
// here so their name, sub-header, URL alias and visibility are admin-editable.
const PANCHANGAM_PAGES = [
  { to: PANCHANGA.to, en: PANCHANGA.en },
  { to: PANCHANGA_PREMIUM.to, en: PANCHANGA_PREMIUM.en },
];
const LEGAL_PAGES = [
  { to: "/about", en: "About Dharmartha" },
  { to: "/terms", en: "Terms of Service" },
  { to: "/privacy", en: "Privacy Policy" },
  { to: "/disclaimer", en: "Disclaimer" },
  { to: "/dpdp", en: "DPDP Act Notice" },
];

// Professional / "Work With Us" pages — reachable from the Collaborate page,
// registered here so admins can rename, alias and hide/unhide them.
const WORK_WITH_US_PAGES = [
  { to: "/join-as-purohit", en: "Join as Purohit / Professional" },
  { to: "/pro", en: "Professional Portal" },
];

// Every page users can reach, grouped for the editor. Body editing takes effect
// on pages wired to read it (ritual pages + any that use settings.page()).
function buildPageList() {
  const rows = [];
  const seen = new Set();
  const add = (to, label, group) => {
    if (!to || seen.has(to)) return;
    seen.add(to);
    rows.push({ path: to, label, group });
  };
  add("/", "Homepage", "Core");
  PANCHANGAM_PAGES.forEach((p) => add(p.to, p.en, "Panchangam"));
  NAV.forEach((g) => g.items.forEach((it) => add(it.to, it.en, g.label.en)));
  add(PATRIKA.to, PATRIKA.en, "About & Info");
  ABOUT_LINKS.forEach((l) => add(l.to, l.en, "About & Info"));
  FESTIVAL_GUIDES.forEach((f) => add(f.to, f.en, "Festival Guides"));
  WORK_WITH_US_PAGES.forEach((l) => add(l.to, l.en, "Work With Us"));
  LEGAL_PAGES.forEach((l) => add(l.to, l.en, "Legal & About"));
  return rows;
}

export default function AdminPages() {
  const { reload } = useSettings();
  const [pages, setPages] = useState(null);
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(null);
  const list = useMemo(buildPageList, []);

  useEffect(() => {
    fetchAdminSettings().then((s) => { setPages(s.pages || {}); setSections(s.sections || {}); }).catch(() => setPages({}));
  }, []);

  const isHidden = (path) => sections[path] === false;
  const toggleHide = (path) => setSections((s) => ({ ...s, [path]: s[path] === false ? true : false }));
  const setField = (path, field, lang, value) =>
    setPages((p) => ({ ...p, [path]: { ...(p[path] || {}), [field]: { ...((p[path] || {})[field] || {}), [lang]: value } } }));
  const setSlug = (path, value) => setPages((p) => ({ ...p, [path]: { ...(p[path] || {}), slug: value } }));

  const save = async () => {
    setSaving(true);
    try {
      // Prune empty page entries.
      const clean = {};
      Object.entries(pages).forEach(([k, v]) => {
        const o = {};
        ["title", "subtitle", "explainer", "body"].forEach((f) => {
          const en = v[f]?.en?.trim?.(), hi = v[f]?.hi?.trim?.();
          if (en || hi) o[f] = { en: en || "", hi: hi || "" };
        });
        if (v.slug?.trim?.()) o.slug = v.slug.trim();
        if (Object.keys(o).length) clean[k] = o;
      });
      await adminUpdateSettings({ pages: clean, sections });
      setPages(clean);
      reload();
      toast("Pages saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  if (pages === null) return <p className="py-20 text-center text-ink-3">…</p>;

  const groups = [...new Set(list.map((r) => r.group))];

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-pages">
      <div className="mb-2 flex items-center gap-2.5">
        <LayoutList className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Pages Manager</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-ink-3">
        Show/hide any page (also removes it from the menu &amp; footer), and edit its title, sub-header, explainer,
        body and URL alias. Hide/unhide &amp; alias work everywhere; text shows on pages wired to read it.
      </p>

      {groups.map((grp) => (
        <div key={grp} className="mb-8">
          <h2 className="mb-3 overline-label text-ink-3">{grp}</h2>
          <div className="divide-y divide-line border border-line bg-surface">
            {list.filter((r) => r.group === grp).map((r) => {
              const v = pages[r.path] || {};
              const slug = r.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home";
              const expanded = open === r.path;
              return (
                <div key={r.path} data-testid={`page-row-${slug}`}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button onClick={() => setOpen(expanded ? null : r.path)} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`page-expand-${slug}`}>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-ink-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      <span className="truncate text-sm font-medium text-ink">{v.title?.en || r.label}</span>
                      <span className="truncate text-xs text-ink-3">{r.path}</span>
                      {isHidden(r.path) && <span className="shrink-0 rounded-full bg-ink/10 px-2 py-0.5 text-[10px] uppercase text-ink-3">Hidden</span>}
                    </button>
                    <a href={r.path} target="_blank" rel="noreferrer" className="text-ink-3 hover:text-saffron"><ExternalLink className="h-4 w-4" /></a>
                    <button onClick={() => toggleHide(r.path)} data-testid={`page-hide-${slug}`}
                      className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-xs transition-colors ${isHidden(r.path) ? "border-line text-ink-3 hover:text-ink" : "border-saffron/40 text-saffron"}`}>
                      {isHidden(r.path) ? <><EyeOff className="h-3.5 w-3.5" /> Hidden</> : <><Eye className="h-3.5 w-3.5" /> Visible</>}
                    </button>
                  </div>
                  {expanded && (
                    <div className="space-y-4 border-t border-line bg-parchment/40 px-4 py-4" data-testid={`page-editor-${slug}`}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div><label className="mb-1 block text-xs text-ink-3">Title (English)</label><input className={inp} data-testid={`page-title-en-${slug}`} value={v.title?.en || ""} onChange={(e) => setField(r.path, "title", "en", e.target.value)} placeholder={r.label} /></div>
                        <div><label className="mb-1 block text-xs text-ink-3">Title (हिन्दी)</label><input className={inp} value={v.title?.hi || ""} onChange={(e) => setField(r.path, "title", "hi", e.target.value)} /></div>
                        <div><label className="mb-1 block text-xs text-ink-3">Sub-header (English)</label><input className={inp} value={v.subtitle?.en || ""} onChange={(e) => setField(r.path, "subtitle", "en", e.target.value)} /></div>
                        <div><label className="mb-1 block text-xs text-ink-3">Sub-header (हिन्दी)</label><input className={inp} value={v.subtitle?.hi || ""} onChange={(e) => setField(r.path, "subtitle", "hi", e.target.value)} /></div>
                        <div className="sm:col-span-2"><label className="mb-1 block text-xs text-ink-3">Explainer (English)</label><textarea rows={2} className={inp} data-testid={`page-explainer-en-${slug}`} value={v.explainer?.en || ""} onChange={(e) => setField(r.path, "explainer", "en", e.target.value)} /></div>
                        <div className="sm:col-span-2"><label className="mb-1 block text-xs text-ink-3">Explainer (हिन्दी)</label><textarea rows={2} className={inp} value={v.explainer?.hi || ""} onChange={(e) => setField(r.path, "explainer", "hi", e.target.value)} /></div>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-ink-3">Body (English) — shown on supported pages</label>
                        <RichTextEditor value={v.body?.en || ""} onChange={(html) => setField(r.path, "body", "en", html)} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-ink-3">URL alias (optional) — e.g. <code>/donate</code> redirects to {r.path}</label>
                        <input className={`${inp} max-w-xs`} data-testid={`page-slug-${slug}`} value={v.slug || ""} onChange={(e) => setSlug(r.path, e.target.value)} placeholder="/your-alias" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="sticky bottom-4">
        <button onClick={save} disabled={saving} data-testid="pages-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save pages
        </button>
      </div>
    </div>
  );
}
